import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep, flattenDeep, range } from "lodash";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import {
  IWebsiteBuilderGridSettings,
  TWebsiteBuilderGridCellAnchor,
  WEBSITE_BUILDER_GRID_MAX_COLUMNS,
  WEBSITE_BUILDER_GRID_MAX_GAP,
  WEBSITE_BUILDER_GRID_MAX_ROWS,
  WEBSITE_BUILDER_GRID_MIN_COLUMNS,
  WEBSITE_BUILDER_GRID_MIN_GAP,
  WEBSITE_BUILDER_GRID_MIN_ROWS,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderNumberInput } from "pages/secure/facility/websiteBuilder/editor/components/numberInput/WebsiteBuilderNumberInput";
import { websiteBuilderUpdateContainerSettings, websiteBuilderUpdateGridDimensions } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowDownLeft,
  faArrowDownRight,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowUpLeft,
  faArrowUpRight,
  faCircleDot,
  faCompress,
  faExpand,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { WebsiteBuilderSettingsRow } from "../../../../../helpers/WebsiteBuilderSettingsRow";
import Input from "components/form/input/Input";
import { isWholeNumber } from "helpers/Helpers";
import { IWebsiteBuilderGridCell, websiteBuilderGenerateDefaultGridCell } from "pages/secure/facility/websiteBuilder/editor/nodes/gridCell/websiteBuilderGridCell";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IGridSettingsProps {
  id: string;
  settings: IWebsiteBuilderGridSettings;
  cells: IWebsiteBuilderGridCell[];
  closeSettings: () => void;
}

type TTrackedGridSettings = Omit<IWebsiteBuilderGridSettings, "rows" | "columns">;

interface IGridSettingsCellAnchorOption {
  cellAnchor: TWebsiteBuilderGridCellAnchor;
  icon: IconDefinition;
}

export function GridSettings(props: IGridSettingsProps) {
  const { id, settings, cells, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const trackedSettings: TTrackedGridSettings = useMemo(() => {
    return {
      gap: settings.gap,
      cellAnchor: settings.cellAnchor,
      cellFill: settings.cellFill,
    };
  }, [settings.gap, settings.cellAnchor, settings.cellFill]);

  const [updatedTrackedSettings, setUpdatedTrackedSettings] = useState<TTrackedGridSettings>({
    gap: settings.gap,
    cellAnchor: settings.cellAnchor,
    cellFill: settings.cellFill,
  });

  const cellAnchorOptions: IGridSettingsCellAnchorOption[] = useMemo(() => [
    { cellAnchor: "top-left", icon: faArrowUpLeft },
    { cellAnchor: "top", icon: faArrowUp },
    { cellAnchor: "top-right", icon: faArrowUpRight },
    { cellAnchor: "left", icon: faArrowLeft },
    { cellAnchor: "center", icon: faCircleDot },
    { cellAnchor: "right", icon: faArrowRight },
    { cellAnchor: "bottom-left", icon: faArrowDownLeft },
    { cellAnchor: "bottom", icon: faArrowDown },
    { cellAnchor: "bottom-right", icon: faArrowDownRight },
  ], []);

  function dispatchSettings(prevTrackedSettings: TTrackedGridSettings, nextTrackedSettings: TTrackedGridSettings) {
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "grid",
          settings: {
            ...cloneDeep(prevTrackedSettings),
            rows: settings.rows,
            columns: settings.columns,
          },
        },
        nextContainerSettings: {
          type: "grid",
          settings: {
            ...cloneDeep(nextTrackedSettings),
            rows: settings.rows,
            columns: settings.columns,
          },
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: trackedSettings,
    updatedState: updatedTrackedSettings,
    setUpdatedState: setUpdatedTrackedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function selectDimensions(rows: number, columns: number) {
    if (
      rows >= WEBSITE_BUILDER_GRID_MIN_ROWS &&
      rows <= WEBSITE_BUILDER_GRID_MAX_ROWS &&
      columns >= WEBSITE_BUILDER_GRID_MIN_COLUMNS &&
      columns <= WEBSITE_BUILDER_GRID_MAX_COLUMNS
    ) {
      const prevMatrix: IWebsiteBuilderGridCell[][] = range(settings.rows).map(r => {
        return range(settings.columns).map(c => {
          return cells[(r * settings.columns) + c];
        });
      });

      const nextMatrix: IWebsiteBuilderGridCell[][] = range(rows).map(() => {
        return range(columns).map(() => {
          return websiteBuilderGenerateDefaultGridCell();
        });
      });

      for (let i = 0; i < prevMatrix.length; i++) {
        for (let j = 0; j < prevMatrix[i].length; j++) {
          if (nextMatrix[i]?.[j] != null) {
            nextMatrix[i][j] = prevMatrix[i][j];
          }
        }
      }

      const prevCells = cloneDeep(cells);
      const nextCells = cloneDeep(flattenDeep(nextMatrix));

      dispatch(
        websiteBuilderUpdateGridDimensions(activePageId, deviceView, id, {
          id,
          prevRows: settings.rows,
          nextRows: rows,
          prevColumns: settings.columns,
          nextColumns: columns,
          prevCells,
          nextCells,
        }),
      );
    }
  }

  function renderSettings() {
    return (
      <>
        <GridSettingsDimensionsSelector
          settings={settings}
          selectDimensions={selectDimensions}
        />

        <WebsiteBuilderSettingsRow layout="equal-width">
          <div className="website-builder-grid-settings-cell-anchor">
            <div className="grid-settings-cell-anchor-title">{t("Cell Anchor")}</div>
            <div className="grid-settings-cell-anchor-options">
              {cellAnchorOptions.map(option => (
                <div
                  key={option.cellAnchor}
                  className={classNames("grid-settings-cell-anchor-option", {
                    "option-selected": updatedTrackedSettings.cellAnchor === option.cellAnchor,
                  })}
                  onClick={() => {
                    handleUpdateState(prev => ({
                      ...prev,
                      cellAnchor: option.cellAnchor,
                    }));
                  }}
                >
                  <FontAwesomeIcon icon={option.icon} />
                </div>
              ))}
            </div>
          </div>

          <div className="website-builder-grid-settings-cell-fill">
            <div className="grid-settings-cell-fill-title">{t("Cell Fill")}</div>
            <div className="grid-settings-cell-fill-options">
              <div
                className={classNames("grid-settings-cell-fill-option", {
                  "option-selected": !updatedTrackedSettings.cellFill,
                })}
                onClick={() => {
                  handleUpdateState(prev => ({
                    ...prev,
                    cellFill: false,
                  }));
                }}
              >
                <FontAwesomeIcon icon={faCompress} />
              </div>
              <div
                className={classNames("grid-settings-cell-fill-option", {
                  "option-selected": updatedTrackedSettings.cellFill,
                })}
                onClick={() => {
                  handleUpdateState(prev => ({
                    ...prev,
                    cellFill: true,
                  }));
                }}
              >
                <FontAwesomeIcon icon={faExpand} />
              </div>
            </div>
          </div>
        </WebsiteBuilderSettingsRow>

        <WebsiteBuilderNumberInput
          label={t("Gap")}
          initialValue={updatedTrackedSettings.gap}
          min={WEBSITE_BUILDER_GRID_MIN_GAP}
          max={WEBSITE_BUILDER_GRID_MAX_GAP}
          required
          onChange={gap => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              gap,
            }));
          }}
        />
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Grid style")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}

interface IGridSettingsDimensionsSelectorProps {
  settings: IWebsiteBuilderGridSettings;
  selectDimensions: (rows: number, columns: number) => void;
}

interface IGridSettingsDimensionsSelectorCell {
  rows: number;
  columns: number;
}

const MAX_SELECTOR_ROWS = 10;
const MAX_SELECTOR_COLUMNS = 10;

export function GridSettingsDimensionsSelector(props: IGridSettingsDimensionsSelectorProps) {
  const { settings, selectDimensions } = props;

  const { t } = useTranslation();

  const debouncedSelectDimensionsRef = useRef<NodeJS.Timeout>(null);

  const [hoveredCell, setHoveredCell] = useState<IGridSettingsDimensionsSelectorCell>(null);

  const [rowsInputValue, setRowsInputValue] = useState<string>(String(settings.rows));
  const [columnsInputValue, setColumnsInputValue] = useState<string>(String(settings.columns));

  useEffect(() => {
    return () => {
      clearTimeout(debouncedSelectDimensionsRef.current);
    };
  }, []);

  function isInputValueValid(updatedInputValue: string, min: number, max: number) {
    const numUpdatedInputValue = Number(updatedInputValue);

    return isWholeNumber(updatedInputValue) && numUpdatedInputValue >= min && numUpdatedInputValue <= max;
  }

  function isRowsInputValueValid(updatedRowsInputValue: string) {
    return isInputValueValid(updatedRowsInputValue, WEBSITE_BUILDER_GRID_MIN_ROWS, WEBSITE_BUILDER_GRID_MAX_ROWS);
  }

  function isColumnsInputValueValid(updatedColumnsInputValue: string) {
    return isInputValueValid(updatedColumnsInputValue, WEBSITE_BUILDER_GRID_MIN_COLUMNS, WEBSITE_BUILDER_GRID_MAX_COLUMNS);
  }

  const rowsInputValueValid = isRowsInputValueValid(rowsInputValue);
  const columnsInputValueValid = isColumnsInputValueValid(columnsInputValue);

  function handleSelectDimensions(rows: number, columns: number, debounced: boolean) {
    clearTimeout(debouncedSelectDimensionsRef.current);

    const selectDimensionsCallback = () => {
      selectDimensions(rows, columns);
    };

    if (debounced) {
      debouncedSelectDimensionsRef.current = setTimeout(selectDimensionsCallback, 500);
    } else {
      selectDimensionsCallback();
    }
  }

  function renderCells() {
    const cellDomNodes: JSX.Element[] = [];

    for (let r = 1; r <= MAX_SELECTOR_ROWS; r++) {
      for (let c = 1; c <= MAX_SELECTOR_COLUMNS; c++) {
        const cellId = `${r}, ${c}`;

        const cell: IGridSettingsDimensionsSelectorCell = {
          rows: r,
          columns: c,
        };

        const hovered =
          hoveredCell != null &&
          cell.rows <= hoveredCell.rows &&
          cell.columns <= hoveredCell.columns;

        const active =
          cell.rows <= settings.rows &&
          cell.columns <= settings.columns;

        cellDomNodes.push(
          <OverlayTrigger
            key={cellId}
            placement="bottom"
            delay={{
              show: 500,
              hide: 100,
            }}
            overlay={
              <Tooltip id={`website-builder-grid-settings-dimensions-selector-cell-(${cellId})`}>
                {`(${r}, ${c})`}
              </Tooltip>
            }
          >
            <div
              className={classNames("dimensions-selector-cell", {
                "cell-hovered": hovered,
                "cell-active": active,
              })}
              onMouseEnter={() => setHoveredCell({ rows: r, columns: c })}
              onClick={() => {
                handleSelectDimensions(r, c, false);

                setRowsInputValue(String(r));
                setColumnsInputValue(String(c));
              }}
            />
          </OverlayTrigger>,
        );
      }
    }

    return cellDomNodes;
  }

  function handleInputChange(updatedRowsInputValue: string, updatedColumnsInputValue: string) {
    setRowsInputValue(updatedRowsInputValue);
    setColumnsInputValue(updatedColumnsInputValue);

    if (isRowsInputValueValid(updatedRowsInputValue) && isColumnsInputValueValid(updatedColumnsInputValue)) {
      handleSelectDimensions(Number(updatedRowsInputValue), Number(updatedColumnsInputValue), true);
    }
  }

  return (
    <>
      <WebsiteBuilderSettingsRow layout="equal-width">
        <Input
          label={t("Rows")}
          value={rowsInputValue}
          error={!rowsInputValueValid}
          onChange={e => handleInputChange(e.target.value, columnsInputValue)}
        />
        <Input
          label={t("Columns")}
          value={columnsInputValue}
          error={!columnsInputValueValid}
          onChange={e => handleInputChange(rowsInputValue, e.target.value)}
        />
      </WebsiteBuilderSettingsRow>

      <div
        className="website-builder-grid-settings-dimensions-selector-grid"
        style={{
          gridTemplateColumns: `repeat(${MAX_SELECTOR_COLUMNS}, 1fr)`,
        }}
        onMouseLeave={() => setHoveredCell(null)}
      >
        {renderCells()}
      </div>
    </>
  );
}